import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Location } from '@angular/common';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { InventoryService } from '../../services/inventory.service';
import { GlobalsService } from '../../services/globals.service';
import { PrintService } from '../../services/print.service';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';

type Step = 'upload' | 'mapping' | 'dry_running' | 'dry_results' | 'posting' | 'done';

@Component({
	selector: 'app-batch-cost-update',
	templateUrl: './batch-cost-update.component.html',
	styleUrls: ['./batch-cost-update.component.scss'],
})
export class BatchCostUpdateComponent implements OnInit {
	step: Step = 'upload';
	color = 'blue';
	config: any = {};

	files: File[] = [];
	processing_recs = false;
	uploaddata: Record<string, string>[] = [];
	UploadedHeaders: string[] = [];
	rowNumber = new UntypedFormControl('1');

	mapcols: { field: string; label: string; req: boolean }[] = [];
	mappingForm = new UntypedFormGroup({});
	selectedCostField = 'materialcost';
	/** GL offset account for the type-35 revaluation; '' = server default (config CostUpdateRevalAccount, else COGS). */
	glOffsetAccount = '';
	/** Full GL chart for the offset-account selector. */
	glAccounts: { accountcode: string; accountname: string }[] = [];

	readonly costFieldOptions = [
		{ value: 'materialcost', label: 'Material Cost', gl: true },
		{ value: 'labourcost', label: 'Labour Cost', gl: false },
		{ value: 'overheadcost', label: 'Overhead Cost', gl: false },
		{ value: 'actualcost', label: 'Actual Cost', gl: false },
		{ value: 'purchprice', label: 'Purchase Price (purchdata)', gl: false },
	];

	readonly batchSize = 250;
	readonly mappingPreviewMax = 50;
	readonly dryRunPreviewMax = 500;

	private readonly allowedExtensions = ['.csv', '.xlsx', '.xls'];

	private readonly fieldHeaderSynonyms: Record<string, string[]> = {
		stockid: ['stockid', 'sku', 'itemnumber', 'itemnum', 'item', 'partnumber', 'partnum', 'productcode', 'product', 'stylenum'],
		mfgname: ['mfgname', 'brand', 'mfg', 'make', 'manufacturer', 'mfgcode'],
		mfgpart: ['mfgpart', 'mfg part', 'manufacturerpart', 'mpn', 'oem', 'partno', 'part number'],
		supplierno: ['supplierno', 'supplier', 'vendor', 'vendorno', 'vendorid', 'supplierid', 'suppliercode'],
		cost: ['cost', 'materialcost', 'material cost', 'unitcost', 'unit cost', 'newcost', 'updatedcost', 'labourcost', 'actualcost', 'overheadcost', 'price', 'purchprice', 'purchaseprice'],
	};

	// Dry run
	isDryRunning = false;
	dryRunCurrentBatch = 0;
	dryRunTotalBatches = 0;
	dryRunProcessed = 0;
	dryRunResults: any[] = [];
	dryRunSummary = { wouldUpdate: 0, alreadySame: 0, notFound: 0, invalid: 0 };

	// Post
	isPosting = false;
	postCurrentBatch = 0;
	postTotalBatches = 0;
	postProcessed = 0;
	postUpdated = 0;
	postFailed = 0;
	postSummaryLines: string[] | null = null;

	// Cost Report tab
	selectedTabIndex = 0;
	costReportLoading = false;
	exportingXls = false;
	exportingPdf = false;
	costReportRows: any[] = [];
	private costReportRawRows: any[] = [];
	costReportPriceTypes: string[] = [];
	costReportBrands: any[] = [];
	costReportLines: any[] = [];
	costReportCategories: any[] = [];
	costReportFilterForm = new UntypedFormGroup({
		mfgname: new UntypedFormControl([]),
		lineid: new UntypedFormControl([]),
		categoryid: new UntypedFormControl([]),
		discontinued: new UntypedFormControl(''),
		lowMargin: new UntypedFormControl(''),
	});

	constructor(
		private globalSearchService: GlobalSearchService,
		private inventoryService: InventoryService,
		private printService: PrintService,
		private location: Location,
		private globalsService: GlobalsService,
		private cdr: ChangeDetectorRef
	) {
		this.color = this.globalSearchService.getColor();
		this.config = this.globalsService.getApiConfig();
		this.globalSearchService.configsubscription.subscribe((r: any) => {
			this.color = r?.color ?? this.color;
		});
	}

	ngOnInit(): void {
		this.adjustMapping();
		this.globalsService.getGLAccounts().subscribe((a: any) => {
			this.glAccounts = Array.isArray(a) ? a : [];
		});
		this.inventoryService.getBrands().subscribe((r: any) => {
			this.costReportBrands = r ?? [];
		});
		this.inventoryService.getItemProductlines().subscribe((r: any) => {
			this.costReportLines = r ?? [];
		});
		this.inventoryService.getItemCategories().subscribe((r: any) => {
			this.costReportCategories = r ?? [];
		});
		this.costReportFilterForm.get('lowMargin')!.valueChanges.subscribe(() => {
			this.applyLowMarginFlag();
			this.cdr.markForCheck();
		});
	}

	// ── File handling ──────────────────────────────────────────────────────────

	get dropzoneThemeColor(): string {
		const allowed = new Set(['blue', 'azure', 'purple', 'green', 'red']);
		const c = String(this.color || 'blue').toLowerCase().trim();
		return allowed.has(c) ? c : 'blue';
	}

	formatFileSizeKb(bytes: number): string {
		return (bytes / 1024).toFixed(1);
	}

	onSelect(event: NgxDropzoneChangeEvent): void {
		if (!event) return;
		if (event.rejectedFiles?.length) {
			const names = event.rejectedFiles.map((f) => f.name).join(', ');
			this.globalSearchService.showNotification(`Not added (type or size): ${names}`, 'warning', 'bottom', 'left');
		}
		if (!event.addedFiles?.length) return;

		const valid: File[] = [];
		const invalid: string[] = [];
		for (const f of event.addedFiles) {
			const lower = f.name.toLowerCase();
			if (this.allowedExtensions.some((ext) => lower.endsWith(ext))) {
				valid.push(f);
			} else {
				invalid.push(f.name);
			}
		}
		if (invalid.length) {
			this.globalSearchService.showNotification(`Only CSV and Excel are supported. Skipped: ${invalid.join(', ')}`, 'warning', 'bottom', 'left');
		}
		if (!valid.length) return;

		const first = valid[0];
		if (valid.length > 1) {
			this.globalSearchService.showNotification(`Only one file at a time — using: ${first.name}`, 'info', 'bottom', 'left');
		}
		this.files = [first];
		const run = () => this.processFile();
		typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame(run) : setTimeout(run, 0);
	}

	onRemove(file: File): void {
		this.files = this.files.filter((f) => f !== file);
		if (!this.files.length) this.resetAll();
	}

	clearFile(): void {
		this.files = [];
		this.resetAll();
	}

	rereadFile(): void {
		this.processFile({ userInitiated: true });
	}

	private processFile(options?: { userInitiated?: boolean }): void {
		if (!this.files.length) return;
		this.processing_recs = true;
		this.uploaddata = [];
		this.UploadedHeaders = [];

		this.parseSpreadsheet(this.files[0])
			.then(({ rows, headers }) => {
				this.processing_recs = false;
				if (!rows.length) {
					this.globalSearchService.showNotification('No data rows found. Check the header row number.', 'warning', 'bottom', 'left');
					this.step = 'upload';
					this.cdr.markForCheck();
					return;
				}
				this.UploadedHeaders = headers;
				this.uploaddata = rows;
				this.adjustMapping();
				this.step = 'mapping';
				if (options?.userInitiated) {
					this.globalSearchService.showNotification('File re-read — review column mappings.', 'success', 'bottom', 'left');
				}
				this.cdr.markForCheck();
			})
			.catch(() => {
				this.processing_recs = false;
				this.step = 'upload';
				this.globalSearchService.showNotification('Could not read file. Use CSV or Excel (.csv, .xlsx, .xls).', 'danger', 'bottom', 'left');
				this.cdr.markForCheck();
			});
	}

	private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as ArrayBuffer);
			reader.onerror = () => reject(new Error('read_failed'));
			reader.readAsArrayBuffer(file);
		});
	}

	private parseSpreadsheet(file: File): Promise<{ rows: Record<string, string>[]; headers: string[] }> {
		return this.readFileAsArrayBuffer(file).then((buf) => {
			const wb = XLSX.read(buf, { type: 'array', cellDates: true, dense: false });
			const sheetName = wb.SheetNames[0];
			if (!sheetName) return { rows: [], headers: [] };
			const ws = wb.Sheets[sheetName];
			const headerRow0 = Math.max(0, parseInt(String(this.rowNumber.value ?? '1'), 10) - 1);
			const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { range: headerRow0, defval: '', raw: false });
			const rows: Record<string, string>[] = raw.map((row) => {
				const o: Record<string, string> = {};
				for (const k of Object.keys(row)) {
					if (k === '__rowNum__') continue;
					const v = row[k];
					o[k] = v === null || v === undefined ? '' : String(v).trim();
				}
				return o;
			});
			return { rows, headers: rows.length ? Object.keys(rows[0]) : [] };
		});
	}

	// ── Mapping ────────────────────────────────────────────────────────────────

	private normalizeForMatch(s: string): string {
		return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
	}

	private scoreFieldHeader(field: string, header: string): number {
		const hn = this.normalizeForMatch(header);
		if (!hn) return 0;
		if (hn === this.normalizeForMatch(field)) return 100;
		let best = 0;
		const syns = this.fieldHeaderSynonyms[field];
		if (syns) {
			for (const syn of syns) {
				const sn = this.normalizeForMatch(syn);
				if (!sn) continue;
				if (hn === sn) best = Math.max(best, 92);
				else if (hn.includes(sn) && sn.length >= 4) best = Math.max(best, 72);
				else if (hn.includes(sn) && sn.length >= 3) best = Math.max(best, 52);
				else if (sn.includes(hn) && hn.length >= 4) best = Math.max(best, 58);
			}
		}
		return best;
	}

	applyAutoMapping(options?: { replaceAll?: boolean; onlyEmpty?: boolean }): void {
		if (!this.fileColumnsForMapping.length || !this.mappingForm) return;
		if (options?.replaceAll) {
			this.mapcols.forEach((m) => this.mappingForm.get(m.field)?.setValue(''));
		}
		const pairs: { field: string; header: string; score: number }[] = [];
		for (const m of this.mapcols) {
			for (const h of this.fileColumnsForMapping) {
				const sc = this.scoreFieldHeader(m.field, h);
				if (sc >= 40) pairs.push({ field: m.field, header: h, score: sc });
			}
		}
		pairs.sort((a, b) => b.score - a.score);
		const usedHeaders = new Set<string>();
		const usedFields = new Set<string>();
		if (!options?.replaceAll) {
			this.mapcols.forEach((m) => {
				const v = this.mappingForm.get(m.field)?.value;
				if (v) usedHeaders.add(String(v));
			});
		}
		for (const p of pairs) {
			if (usedHeaders.has(p.header) || usedFields.has(p.field)) continue;
			const ctrl = this.mappingForm.get(p.field);
			if (!ctrl) continue;
			if (options?.onlyEmpty && ctrl.value) continue;
			ctrl.setValue(p.header);
			usedHeaders.add(p.header);
			usedFields.add(p.field);
		}
		this.cdr.markForCheck();
	}

	suggestMappings(): void {
		this.applyAutoMapping({ replaceAll: true });
		this.globalSearchService.showNotification('Suggested column mappings applied — review before Dry Run.', 'info', 'bottom', 'left');
	}

	get isPurchpriceMode(): boolean {
		return this.selectedCostField === 'purchprice';
	}

	get postGlNote(): boolean {
		return this.costFieldOptions.find((o) => o.value === this.selectedCostField)?.gl === true;
	}

	adjustMapping(): void {
		const prev: Record<string, string> = {};
		if (this.mappingForm) {
			for (const k of Object.keys(this.mappingForm.controls)) {
				const v = this.mappingForm.get(k)?.value;
				if (v && this.fileColumnsForMapping.includes(String(v))) prev[k] = String(v);
			}
		}
		const cols: { field: string; label: string; req: boolean }[] = [
			{ field: 'stockid', label: 'Item number (optional if brand + part)', req: false },
			{ field: 'mfgname', label: 'Brand', req: false },
			{ field: 'mfgpart', label: 'Manufacturer part number', req: false },
		];
		if (this.isPurchpriceMode) {
			cols.push({ field: 'supplierno', label: 'Supplier # (optional — uses preferred if blank)', req: false });
		}
		cols.push({ field: 'cost', label: this.isPurchpriceMode ? 'New purchase price' : 'New cost value', req: true });
		this.mapcols = cols;
		this.mappingForm = new UntypedFormGroup({});
		this.mapcols.forEach((m) => {
			this.mappingForm.addControl(m.field, new UntypedFormControl(prev[m.field] ?? ''));
		});
		if (this.fileColumnsForMapping.length) {
			this.applyAutoMapping({ onlyEmpty: true });
		}
		this.cdr.markForCheck();
	}

	get fileColumnsForMapping(): string[] {
		return (this.UploadedHeaders || []).filter((k) => k !== '_sourceFile' && k !== '__rowNum__');
	}

	get mappingPreviewRows(): Record<string, string>[] {
		return this.uploaddata.slice(0, this.mappingPreviewMax);
	}

	get mappingPreviewTotal(): number {
		return this.uploaddata.length;
	}

	getSystemFieldForFileHeader(fileHeader: string): string {
		const m = this.mapcols.find((mc) => this.mappingForm?.get(mc.field)?.value === fileHeader);
		return m ? m.field : '';
	}

	isFieldMappedToOtherHeader(fieldKey: string, currentFileHeader: string): boolean {
		const v = this.mappingForm?.get(fieldKey)?.value;
		return !!v && String(v) !== currentFileHeader;
	}

	onFileColumnMappingChange(fileHeader: string, newField: string): void {
		if (!this.mappingForm) return;
		this.mapcols.forEach((mc) => {
			const c = this.mappingForm.get(mc.field);
			if (c?.value === fileHeader) c.setValue('');
		});
		if (newField) this.mappingForm.get(newField)?.setValue(fileHeader);
		this.cdr.markForCheck();
	}

	get canRunDryRun(): boolean {
		if (!this.mappingForm) return false;
		const v = this.mappingForm.value as Record<string, string>;
		if (!v.cost) return false;
		return !!(v.stockid || (v.mfgname && v.mfgpart));
	}

	get selectedCostFieldLabel(): string {
		return this.costFieldOptions.find((o) => o.value === this.selectedCostField)?.label ?? this.selectedCostField;
	}

	onCostFieldChange(): void {
		this.adjustMapping();
	}

	downloadTemplate(): void {
		const csv = this.isPurchpriceMode
			? 'stockid,mfgname,mfgpart,supplierno,cost\n'
			: 'stockid,mfgname,mfgpart,cost\n';
		const filename = this.isPurchpriceMode ? 'batch_purchprice_update_template.csv' : 'batch_cost_update_template.csv';
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		FileSaver.saveAs(blob, filename);
	}

	// ── Dry run ────────────────────────────────────────────────────────────────

	private buildMappedRows(): Record<string, string>[] {
		const mapping = this.mappingForm.value as Record<string, string>;
		return this.uploaddata
			.map((e) => {
				const line: Record<string, string> = {};
				Object.entries(mapping).forEach(([key, value]) => {
					if (value) line[key] = String(e[value] ?? '').trim();
				});
				return line;
			})
			.filter((r) => r.cost);
	}

	async startDryRun(): Promise<void> {
		if (!this.canRunDryRun) return;
		const rows = this.buildMappedRows();
		if (!rows.length) {
			this.globalSearchService.showNotification('No rows with a cost value found.', 'warning', 'bottom', 'left');
			return;
		}

		this.step = 'dry_running';
		this.isDryRunning = true;
		this.dryRunResults = [];
		this.dryRunProcessed = 0;
		this.dryRunSummary = { wouldUpdate: 0, alreadySame: 0, notFound: 0, invalid: 0 };

		const batches: Record<string, string>[][] = [];
		for (let i = 0; i < rows.length; i += this.batchSize) {
			batches.push(rows.slice(i, i + this.batchSize));
		}
		this.dryRunTotalBatches = batches.length;
		this.dryRunCurrentBatch = 0;

		for (let b = 0; b < batches.length; b++) {
			this.dryRunCurrentBatch = b + 1;
			this.cdr.markForCheck();
			try {
				const result: any = await this.inventoryService
					.batchCostUpdateDryrun({ costField: this.selectedCostField, rows: batches[b] })
					.toPromise();
				if (result?.rows) {
					this.dryRunResults.push(...result.rows);
					this.dryRunProcessed += batches[b].length;
				}
			} catch {
				this.dryRunProcessed += batches[b].length;
			}
			await this.yieldUi();
		}

		for (const r of this.dryRunResults) {
			if (r.status === 'update') this.dryRunSummary.wouldUpdate++;
			else if (r.status === 'same') this.dryRunSummary.alreadySame++;
			else if (r.status === 'notfound') this.dryRunSummary.notFound++;
			else if (r.status === 'invalid') this.dryRunSummary.invalid++;
		}

		this.isDryRunning = false;
		this.step = 'dry_results';
		this.cdr.markForCheck();
	}

	get dryRunProgressPct(): number {
		if (!this.dryRunTotalBatches) return 0;
		return Math.round((this.dryRunCurrentBatch / this.dryRunTotalBatches) * 100);
	}

	get dryRunPreviewRows(): any[] {
		return this.dryRunResults.slice(0, this.dryRunPreviewMax);
	}

	backToMapping(): void {
		this.step = 'mapping';
		this.dryRunResults = [];
		this.dryRunSummary = { wouldUpdate: 0, alreadySame: 0, notFound: 0, invalid: 0 };
	}

	// ── Posting ────────────────────────────────────────────────────────────────

	async startPosting(): Promise<void> {
		const toPost = this.dryRunResults.filter((r) => r.status === 'update');
		if (!toPost.length) {
			this.globalSearchService.showNotification('Nothing to update — all rows are already current.', 'info', 'bottom', 'left');
			return;
		}

		this.step = 'posting';
		this.isPosting = true;
		this.postProcessed = 0;
		this.postUpdated = 0;
		this.postFailed = 0;
		this.postSummaryLines = null;

		const batches: any[][] = [];
		for (let i = 0; i < toPost.length; i += this.batchSize) {
			batches.push(toPost.slice(i, i + this.batchSize));
		}
		this.postTotalBatches = batches.length;
		this.postCurrentBatch = 0;

		for (let b = 0; b < batches.length; b++) {
			this.postCurrentBatch = b + 1;
			this.cdr.markForCheck();
			try {
				const rows = batches[b].map((r: any) => ({
					stockid: r.stockid,
					cost: r.proposedCost,
					...(this.isPurchpriceMode && r.supplierno ? { supplierno: r.supplierno } : {}),
				}));
				const result: any = await this.inventoryService
					.batchCostUpdateProcess({
						costField: this.selectedCostField,
						rows,
						...(this.postGlNote && this.glOffsetAccount ? { glaccount: this.glOffsetAccount } : {}),
					})
					.toPromise();
				this.postProcessed += batches[b].length;
				if (result) {
					this.postUpdated += result.updated ?? 0;
					this.postFailed += result.failed ?? 0;
				}
			} catch {
				this.postProcessed += batches[b].length;
				this.postFailed += batches[b].length;
			}
			await this.yieldUi();
		}

		this.isPosting = false;
		this.postSummaryLines = [
			`Target field: ${this.selectedCostFieldLabel}`,
			`Total processed: ${this.postProcessed}`,
			`Updated: ${this.postUpdated}`,
			`Failed: ${this.postFailed}`,
		];
		this.step = 'done';
		this.cdr.markForCheck();
	}

	get postProgressPct(): number {
		if (!this.postTotalBatches) return 0;
		return Math.round((this.postCurrentBatch / this.postTotalBatches) * 100);
	}

	startOver(): void {
		this.resetAll();
	}

	// ── Helpers ────────────────────────────────────────────────────────────────

	private yieldUi(): Promise<void> {
		return new Promise((resolve) => {
			typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame(() => resolve()) : setTimeout(() => resolve(), 0);
		});
	}

	private resetAll(): void {
		this.step = 'upload';
		this.files = [];
		this.uploaddata = [];
		this.UploadedHeaders = [];
		this.processing_recs = false;
		this.dryRunResults = [];
		this.postSummaryLines = null;
		this.isDryRunning = false;
		this.isPosting = false;
		this.dryRunSummary = { wouldUpdate: 0, alreadySame: 0, notFound: 0, invalid: 0 };
		this.adjustMapping();
	}

	// ── Cost Report ───────────────────────────────────────────────────────────

	loadCostReport(): void {
		this.costReportLoading = true;
		this.costReportRows = [];
		this.costReportRawRows = [];
		this.costReportPriceTypes = [];
		const { lowMargin, ...serverFilters } = this.costReportFilterForm.value;
		this.inventoryService.batchCostExportData(serverFilters).subscribe((r: any) => {
			this.costReportRawRows = r?.items ?? [];
			this.costReportPriceTypes = (r?.salestypes ?? []).map((st: any) => st.typeabbrev);
			this.applyLowMarginFlag();
			this.costReportLoading = false;
			this.cdr.markForCheck();
		});
	}

	private applyLowMarginFlag(): void {
		const raw = this.costReportFilterForm.get('lowMargin')!.value;
		const threshold = raw !== '' && raw !== null ? parseFloat(raw) : null;
		this.costReportRows = this.costReportRawRows.map((row) => ({
			...row,
			lowMargin: threshold !== null && !isNaN(threshold) && row.margin !== '' && parseFloat(row.margin) < threshold,
		}));
	}

	private buildCostReportHtml(): string {
		const cols = [
			{ key: 'stockid', label: 'Item #' },
			{ key: 'description', label: 'Description' },
			{ key: 'mfgname', label: 'Brand' },
			{ key: 'lineid', label: 'Line' },
			{ key: 'categoryid', label: 'Category' },
			{ key: 'materialcost', label: 'Material Cost' },
			{ key: 'labourcost', label: 'Labour Cost' },
			{ key: 'overheadcost', label: 'Overhead Cost' },
			{ key: 'actualcost', label: 'Actual Cost' },
			{ key: 'xrefcost', label: 'Xref Cost' },
			{ key: 'lastcost', label: 'Last Cost' },
			{ key: 'total_qoh', label: 'QOH' },
			{ key: 'supplierno', label: 'Supplier' },
			{ key: 'purchprice', label: 'Purch Price' },
			{ key: 'defaultSellPrice', label: 'Sell Price' },
			{ key: 'margin', label: 'Margin %' },
			{ key: 'discontinued', label: 'Disc' },
			{ key: 'enabled', label: 'Enabled' },
		];
		const priceColKeys = this.costReportPriceTypes.map((pt) => `price_${pt}`);

		let html = '<table class="table table-fixed table-sm">';
		html += '<thead><tr>';
		cols.forEach((c) => { html += `<th>${c.label}</th>`; });
		this.costReportPriceTypes.forEach((pt) => { html += `<th>${pt}</th>`; });
		html += '</tr></thead><tbody>';

		for (const row of this.costReportRows) {
			html += '<tr>';
			cols.forEach((c) => { html += `<td>${row[c.key] ?? ''}</td>`; });
			priceColKeys.forEach((k) => { html += `<td>${row[k] ?? ''}</td>`; });
			html += '</tr>';
		}
		html += '</tbody></table>';
		return html;
	}

	exportCostReportXls(): void {
		if (this.exportingXls) return;
		this.exportingXls = true;
		this.cdr.markForCheck();
		const today = new Date();
		const encoded = this.globalSearchService.base64encode(this.buildCostReportHtml());
		const data = {
			content: encoded,
			filename: `cost_report_${today.getFullYear()}${today.getMonth() + 1}${today.getDate()}`,
			title: 'Cost Report',
			subtitle: `Generated ${today.toLocaleString()}`,
		};
		this.printService.xls(data).subscribe({
			next: (result: any) => {
				this.globalSearchService.downloadXls(result.content, data.filename);
				this.exportingXls = false;
				this.cdr.markForCheck();
			},
			error: () => {
				this.exportingXls = false;
				this.cdr.markForCheck();
			},
		});
	}

	exportCostReportPdf(): void {
		if (this.exportingPdf) return;
		this.exportingPdf = true;
		this.cdr.markForCheck();
		const today = new Date();
		const encoded = this.globalSearchService.base64encode(this.buildCostReportHtml());
		const data = {
			content: encoded,
			filename: `cost_report_${today.getFullYear()}${today.getMonth() + 1}${today.getDate()}`,
			title: 'Cost Report',
			subtitle: `Generated ${today.toLocaleString()}`,
		};
		this.printService.pdf(data).subscribe({
			next: (result: any) => {
				this.globalSearchService.downloadPdf(result.content, data.filename);
				this.exportingPdf = false;
				this.cdr.markForCheck();
			},
			error: () => {
				this.exportingPdf = false;
				this.cdr.markForCheck();
			},
		});
	}

	back(): void {
		this.location.back();
	}
}
